import union from 'lodash/union';

import {
    CUSTOM_CONTENT_TYPE_CONTRIBUTION_MODES,
    CUSTOM_CONTENT_TYPE_PERMISSIONS,
} from '@lumapps/content-types/permissions';

import { PermissionsState } from '../ducks/types';
import { getPermissionKey } from '../utils';

const computePermissions = (initState: any = {}) => {
    const { myPermissions = {}, user, userRoles = [] } = initState;
    const configuredAuthorizations: Record<string, boolean> = {};
    const canEditOnlyOwnContentAuthorizations: Record<string, boolean> = {};
    const canDesignContentTypeAuthorizations: Record<string, boolean> = {};
    const userRoleFeedRestrictions: Record<string, string[]> = {};
    // Tracks keys where at least one authorization has no feed restriction.
    // An unrestricted authorization grants access to all feeds, so no feed check
    // should apply for that key — mirroring the legacy per-authorization iteration
    // that returns true as soon as one authorization passes.
    const unrestrictedFeedKeys: Record<string, true> = {};
    const userRoleMetadataRestrictions: Record<string, string[]> = {};
    // One authorization without a metadata restriction allows every metadata of that content type.
    // It cancels any restriction from the other authorizations.
    const unrestrictedMetadataContentTypes: Record<string, true> = {};

    /**
     * User roles determine what sections of the application we can access to if you have that
     * role assigned. Each role has a list of authorizations, where each authorization has a list
     * of actions that the user can perform.
     *
     * In order to avoid iterating through these roles each time we want to know the
     * permissions that a user has, we run it once on startup, and we create a simplified list
     * of actions using a Record<string, boolean> where the key is the action and the value is
     * if the user is allowed to execute the action or not. This should lower the computation
     * time from O(nˆ3) to O(1) while evaluating each action. We process this information on startup,
     * but during the course of the application, it is more efficient.
     *
     * TODO: we will want to migrate this code to the backend.
     */
    if (userRoles) {
        userRoles.forEach((userRole: any) => {
            const { authorizations = [] } = userRole;

            authorizations.forEach((authorization: any) => {
                const { actions = [], canEditOnlyOwnContent, contributionMode, feeds, metadata } = authorization;
                actions.forEach((action: any) => {
                    const { name, type, customContentType } = action;
                    const fullActionStr = getPermissionKey(name, type, customContentType);

                    configuredAuthorizations[fullActionStr] = true;

                    if (feeds?.length) {
                        if (!unrestrictedFeedKeys[fullActionStr]) {
                            userRoleFeedRestrictions[fullActionStr] = union(
                                userRoleFeedRestrictions[fullActionStr],
                                feeds,
                            );
                        }
                    } else {
                        // Auth without feed restriction: cancel any previously accumulated restriction
                        // for this key and prevent future restricted auths from overwriting it.
                        unrestrictedFeedKeys[fullActionStr] = true;
                        delete userRoleFeedRestrictions[fullActionStr];
                    }

                    // Metadata restrictions are scoped by content type, not by action.
                    // Every authorization on the content type contributes, whatever its action.
                    if (customContentType && !unrestrictedMetadataContentTypes[customContentType]) {
                        if (!metadata?.length) {
                            unrestrictedMetadataContentTypes[customContentType] = true;
                            delete userRoleMetadataRestrictions[customContentType];
                        } else {
                            userRoleMetadataRestrictions[customContentType] = union(
                                userRoleMetadataRestrictions[customContentType],
                                metadata,
                            );
                        }
                    }

                    // If this authorization is unrestricted (canEditOnlyOwnContent: false), explicitly
                    // write false to cancel any restriction set by a previous authorization for the same
                    // action. If restricted and not already cancelled, set to true. This mirrors the
                    // legacy which grants access if ANY authorization passes — an unrestricted one wins.
                    if (!canEditOnlyOwnContent) {
                        canEditOnlyOwnContentAuthorizations[fullActionStr] = false;
                    } else if (canEditOnlyOwnContentAuthorizations[fullActionStr] !== false) {
                        canEditOnlyOwnContentAuthorizations[fullActionStr] = true;
                    }

                    if (
                        fullActionStr.startsWith(CUSTOM_CONTENT_TYPE_PERMISSIONS.CUSTOM_CONTENT_EDIT) &&
                        contributionMode === CUSTOM_CONTENT_TYPE_CONTRIBUTION_MODES.DESIGNER
                    ) {
                        // If at least one EDIT authorization allows to design, then the user is considered a designer.
                        canDesignContentTypeAuthorizations[customContentType] = true;
                    }
                });
            });
        });
    }

    const permissions: PermissionsState = {
        isSuperAdmin: user?.isSuperAdmin,
        instancesSuperAdmin: user ? user.instancesSuperAdmin : [],
        authorizations: { ...configuredAuthorizations, ...myPermissions },
        canEditOnlyOwnContentAuthorizations,
        canDesignContentTypeAuthorizations,
        userRoleFeedRestrictions,
        userRoleMetadataRestrictions,
    };

    Object.assign(initState, {
        permissions,
    });

    return initState;
};

export { computePermissions };
